import { useCallback, useEffect, useRef, useState } from 'react'

import { CatalogBlock } from '@/types'

interface ScrollRef {
  scrollToIndex: (index: number, align?: 'start' | 'center' | 'end') => void
}

export const useCatalogNavigation = (blocks: CatalogBlock[]) => {
  const [selectedBlockId, setSelectedBlockId] = useState<string>()
  const categoryRefs = useRef<Record<string, HTMLDivElement>>({})
  const virtualizedScrollRef = useRef<ScrollRef | null>(null)
  const lastNavigatedHash = useRef<string>('')

  const selectBlock = useCallback((blockId?: string) => {
    setSelectedBlockId(blockId)
  }, [])

  const clearSelectedBlock = useCallback(() => {
    setSelectedBlockId(undefined)
  }, [])

  const handleNavigate = useCallback(
    (blockId: string) => {
      selectBlock(blockId)
      lastNavigatedHash.current = blockId

      // For virtualized mode, find the index of the block
      const index = blocks.findIndex((block) => block.id === blockId)
      if (index !== -1) {
        virtualizedScrollRef.current?.scrollToIndex(index, 'start')
      }

      // Fallback: also try to scroll using the ref if available
      setTimeout(() => {
        const targetRef = categoryRefs.current[blockId]
        if (targetRef) {
          targetRef.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }

        // Remove hash from URL after scroll handling
        if (window.location.hash) {
          window.history.replaceState(
            null,
            '',
            window.location.pathname + window.location.search,
          )
        }
      }, 100)
    },
    [blocks, selectBlock],
  )

  // Listen for URL hash changes
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1) // Remove the # character
      if (
        hash &&
        hash !== lastNavigatedHash.current &&
        blocks.some((block) => block.id === hash)
      ) {
        handleNavigate(hash)
      }
    }

    // Check for initial hash on mount
    handleHashChange()

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange)
    // Also listen for popstate as a fallback for Next.js router navigation
    window.addEventListener('popstate', handleHashChange)

    // Check hash periodically as a workaround for Next.js not triggering hashchange
    const intervalId = setInterval(() => {
      const currentHash = window.location.hash.slice(1)
      if (
        currentHash &&
        currentHash !== lastNavigatedHash.current &&
        blocks.some((block) => block.id === currentHash)
      ) {
        handleNavigate(currentHash)
      }
    }, 100)

    return () => {
      window.removeEventListener('hashchange', handleHashChange)
      window.removeEventListener('popstate', handleHashChange)
      clearInterval(intervalId)
    }
  }, [blocks, handleNavigate])

  const setVirtualizedScrollRef = useCallback((scrollRef: ScrollRef) => {
    virtualizedScrollRef.current = scrollRef
  }, [])

  return {
    selectedBlockId,
    setSelectedBlockId,
    selectBlock,
    clearSelectedBlock,
    categoryRefs,
    handleNavigate,
    setVirtualizedScrollRef,
  }
}
